import { useEffect } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { History } from 'lucide-react';
import { Button } from 'components/ui/button';
import AuditDrawerFrame from 'components/admin/audit/AuditDrawerFrame';
import AuditTimeline from 'components/admin/audit/AuditTimeline';
import { useAdminAuditEvents } from 'hooks/useAdminAuditEvents';
import { actorLabel, dayKey, formatTime, numericDate } from 'utils/auditLabels';
import type { AdminAuditEvent } from 'api/adminAuditApi';

/** CA16 — Échap or the close button both return focus to this trigger. */
export const HISTORY_BUTTON_ID = 'technician-history-action';

/** "29/09/2026 à 14:32" — Paris time, no seconds (design §5.1), the connector translated (CA26). */
export const lastModifiedText = (t: TFunction, event: AdminAuditEvent): string =>
  t('Last modified on {{date}} by {{name}}', {
    date: t('{{date}} at {{time}}', { date: numericDate(dayKey(event.occurredAt)), time: formatTime(event.occurredAt) }),
    name: actorLabel(t, event.actor, event.context),
  });

interface HistoryDrawerProps {
  entityType: string;
  entityId: string;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Bubbles the latest event up so the record can place "Dernière modification" under its own h1. */
  onLatestEvent?: (event: AdminAuditEvent | null) => void;
}

/**
 * "Historique" trigger + count badge and the 560 px panel it opens (J12, design §5.1-5.2), as one
 * component: the hook it owns only ever mounts under `dashboard:audit:view` (CA10), so the eight
 * other `AdminTechnician` suites — none of which grant it — never need a `QueryClientProvider`.
 */
const HistoryDrawer = ({ entityType, entityId, title, open, onOpenChange, onLatestEvent }: HistoryDrawerProps) => {
  const { t } = useTranslation();
  const audit = useAdminAuditEvents({ entityType, entityId });
  const ready = !audit.isLoading && !audit.isError; // CA18 — no count while loading or on error.

  useEffect(() => {
    onLatestEvent?.(ready ? audit.events[0] ?? null : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, audit.events]);

  return (
    <>
      <Button id={HISTORY_BUTTON_ID} type="button" variant="outline" aria-haspopup="dialog" aria-expanded={open} onClick={() => onOpenChange(true)}>
        <History className="mr-1.5 h-4 w-4" aria-hidden="true" />
        {t('History')}
        {ready && (
          <span className="ml-1.5 inline-flex h-[18px] items-center rounded-[6px] bg-accent px-1.5 text-[11.5px] font-semibold text-muted-foreground">
            {audit.total}
          </span>
        )}
      </Button>

      <AuditDrawerFrame open={open} onOpenChange={onOpenChange} title={t('History')} description={title}>
        <AuditTimeline
          events={audit.events}
          total={audit.total}
          since={audit.since}
          retentionMonths={audit.retentionMonths}
          isLoading={audit.isLoading}
          isError={audit.isError}
          error={audit.error}
          onRetry={() => audit.refetch()}
          hasNextPage={!!audit.hasNextPage}
          isFetchingNextPage={audit.isFetchingNextPage}
          isFetchNextPageError={audit.isFetchNextPageError}
          onLoadMore={() => audit.fetchNextPage()}
        />
      </AuditDrawerFrame>
    </>
  );
};

export default HistoryDrawer;
